<template>
	<div>
		<j-base-multi-select-with-flexible-value
			:value="computedValue"
			:options="options.entities"
			:size="size"
			:status="status"
			:allowMultiselect="false"
			:allowLocalSearch="false"
			:isLoading="isLoading"
			:placeholder="$t('search')"
			@input="onInput"
			@search="onSearch"
		/>
	</div>
</template>

<script>
import JBaseMultiSelectWithFlexibleValue from "@/components/JBaseMultiSelectWithFlexibleValue"
import api from "@/api/gql.js"
import debounce from "lodash.debounce"

export default {
	name: "SelectEntity",
	components: { JBaseMultiSelectWithFlexibleValue },
	props: {
		/**
		 * this value represents entityId
		 */
		value: {
			type: String,
			default: ""
		},

		/**
		 * Status of the input
		 */
		status: {
			type: String,
			default: null
		},

		/**
		 * What entity to manipulate
		 * Based on this we call different APIs
		 */
		entityType: {
			validator(val) {
				return [
					"individual",
					"company",
					"object",
					"organization",
					"participant"
				].includes(val) // TODO: internal, organizations
			},
			default: "individual"
		},

		/**
		 * Size of the form inputs
		 */
		size: {
			validator(val) {
				return ["xsmall", "small", "medium", "large"].includes(val)
			},
			default: "medium"
		}
	},

	data() {
		return {
			options: {
				entities: []
			},
			isLoading: false
		}
	},

	computed: {
		/**
		 * Composes this.value into an array that multi-select is expecting
		 */
		computedValue() {
			if (this.value?.[0])
				return [
					{
						name: this.computedName,
						value: this.value?.[0]?.value
					}
				]

			return []
		},

		/**
		 * It will find the proper name in this.options.entities
		 * based on this.value (which is entityId)
		 */
		computedName() {
			return (
				this.options.entities?.find(item => item.id === this.value)?.name ||
				"(No name)"
			)
		}
	},

	methods: {
		onInput(value) {
			this.$emit("input", value?.[0]?.id)
		},

		onSearch: debounce(function(searchTerm) {
			this.fetchEntities(searchTerm)
		}, 400),

		/**
		 * search term will be passed from when user searches in select box
		 * id will be passed in watcher when value changes so that we can fetch proper name in select box
		 */
		async fetchEntities(searchTerm, id) {
			// Define api keys per each entity
			let apiKeys = { q: "individuals", w: "individualWhereInput" }
			if (this.entityType === "company")
				apiKeys = { q: "companies", w: "companyWhereInput" }
			if (this.entityType === "object")
				apiKeys = { q: "objects", w: "objectWhereInput" }
			if (this.entityType === "organization")
				apiKeys = { q: "organizations", w: "organizationWhereInput" }
			if (this.entityType === "participant")
				apiKeys = { q: "internals", w: "internalWhereInput" }

			// Universal query
			const query = `
				query ${apiKeys.q}Options($where: ${apiKeys.w}) {
					${apiKeys.q}(where: $where) {
						list {
							id
							name
						}
					}
				}
      `

			this.isLoading = true
			const variables = {
				where: { name_contains: searchTerm, id }
			}
			const res = await api({ query, variables })
			this.isLoading = false

			// Save
			this.options.entities = res?.data?.[apiKeys.q]?.list
		}
	},

	watch: {
		/**
		 * When value changes, fetch options so that we can display proper name in select box
		 */
		value: {
			handler() {
				if (!this.value) return
				this.fetchEntities(null, this.value)
			},
			immediate: false
		}
	},

	/**
	 * When component mounts, fetch entities so that we can display
	 * proper name in select box if value exists
	 */
	mounted() {
		if (this.value) this.fetchEntities(null, this.value)
	}
}
</script>
